import React, { useState, useEffect } from "react";
import { Table, Container, Collapse, Card, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEdit, faTimes, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { useAppContext } from "../../../context/appContext";
import { isViewOnly } from "../../../utils/permissions";
import { Paginate } from "../../../components";
import TooltipInfo from "../../../components/TooltipInfo";
import { EditGroupVersionModal, DeleteGroupVersionModal } from "./UpdateGroupVersion";

const GroupVersionsListPage = ({ env, handleEnvChange, currentTenant, groupVersions, isLoading, isError }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedGroupVersion, setSelectedGroupVersion] = useState(null);
  const [expandedVersions, setExpandedVersions] = useState({});

  const { user } = useAppContext();
  const viewOnly = isViewOnly(user);

  const currentTenantName = localStorage.getItem("selectedTenantName") || null;

  const itemsPerPage = 10;
  const indexOfLastVersion = currentPage * itemsPerPage;
  const indexOfFirstVersion = indexOfLastVersion - itemsPerPage;
  const currentGroupVersions = groupVersions?.slice(indexOfFirstVersion, indexOfLastVersion);
  const totalPages = groupVersions?.length ? Math.ceil(groupVersions?.length / itemsPerPage) : 0;

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleEditClick = (groupVersion) => {
    setSelectedGroupVersion(groupVersion);
    setShowEditModal(true);
  };

  const handleDeleteClick = (groupVersion) => {
    setSelectedGroupVersion(groupVersion);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedGroupVersion(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedGroupVersion(null);
  };

  const handleViewClick = (groupVersion) => {
    setSelectedGroupVersion(groupVersion);
    setShowEditModal(true);
  };

  const toggleExpand = (id) => {
    setExpandedVersions((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [groupVersions]);

  const isTenantSelected =
    currentTenantName !== null &&
    String(currentTenantName) !== "undefined" &&
    String(currentTenantName) !== "null" &&
    currentTenantName !== "";

  const tenantHasGroupVersions = isTenantSelected && groupVersions?.length >= 1;

  if (isLoading || isError || !isTenantSelected) {
    return isLoading ? (
      <Container>
        <p>Loading group versions...</p>
      </Container>
    ) : isError ? (
      <Container>
        <h5>No group versions found</h5>
      </Container>
    ) : (
      <Container>
        <h5>Choose a tenant to view OGMP group versions.</h5>
      </Container>
    );
  }

  return (
    <>
      {tenantHasGroupVersions ? (
        <Container fluid>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th scope="col">Group Name</th>
                <th scope="col">OGMP Year</th>
                <th scope="col">OGMP Level</th>
                <th scope="col">Planned Site Surveys</th>
                <th scope="col">Uncertainty Fraction</th>
                <th scope="col">Production CH4</th>
                <th scope="col">Unit</th>
                <th scope="col">Type of Equity</th>
                <th scope="col">Equity Fraction</th>
                <th scope="col">Version UUID</th>
                <th scope="col" style={{ width: "80px" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentGroupVersions?.map((version) => (
                <React.Fragment key={version.id}>
                  <tr
                    onClick={() => toggleExpand(version.id)}
                    style={{ cursor: "pointer" }}
                    className={expandedVersions[version.id] ? "table-active" : ""}
                  >
                    <td>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span>{version.name}</span>
                        <FontAwesomeIcon icon={expandedVersions[version.id] ? faChevronUp : faChevronDown} size="sm" />
                      </div>
                    </td>
                    <td>{version.ogmpYear}</td>
                    <td>{version.ogmpLevel}</td>
                    <td>{version.plannedSiteSurveys || ""}</td>
                    <td>{version.uncertaintyFraction || ""}</td>
                    <td>{version.productionCh4 || ""}</td>
                    <td>{version.productionCh4Unit || ""}</td>
                    <td>{version.typeOfEquity || ""}</td>
                    <td>{version.equityFraction || ""}</td>
                    <td>{version.id}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="d-flex justify-content-around align-items-center" style={{ minWidth: "80px" }}>
                        {viewOnly ? (
                          <FontAwesomeIcon icon={faEye} style={{ cursor: "pointer" }} onClick={() => handleViewClick(version)} />
                        ) : (
                          <>
                            <FontAwesomeIcon
                              icon={faEdit}
                              style={{ cursor: "pointer", marginRight: "10px" }}
                              onClick={() => handleEditClick(version)}
                            />
                            <FontAwesomeIcon
                              icon={faTimes}
                              style={{ cursor: "pointer", color: "red" }}
                              onClick={() => handleDeleteClick(version)}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="11" className="p-0">
                      <Collapse in={expandedVersions[version.id]}>
                        <div className="p-3 bg-light w-100">
                          <Card className="mb-3">
                            <Card.Body>
                              <div className="row">
                                <div className="col-md-3">
                                  <p>
                                    <strong>Asset Type:</strong> {version.assetType || "N/A"}
                                  </p>
                                </div>
                                <div className="col-md-2">
                                  <p>
                                    <strong>Segment:</strong> {version.segment || "N/A"}
                                  </p>
                                </div>
                                <div className="col-md-2">
                                  <p>
                                    <strong>Operator:</strong> {version.operator || "N/A"}
                                  </p>
                                </div>
                                <div className="col-md-2">
                                  <p>
                                    <strong>Location:</strong> {version.location || "N/A"}
                                  </p>
                                </div>
                                <div className="col-md-12">
                                  <Form.Group controlId="versionComments">
                                    <Form.Label>
                                      <strong>Comments:</strong>
                                    </Form.Label>
                                    <Form.Control
                                      as="textarea"
                                      rows={3}
                                      value={version.comments || ""}
                                      readOnly
                                      placeholder="No comments"
                                    />
                                  </Form.Group>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </Table>

          <Paginate currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />

          {/* Modals */}
          {selectedGroupVersion && (
            <>
              <EditGroupVersionModal
                handleEnvChange={handleEnvChange}
                show={showEditModal}
                groupVersion={selectedGroupVersion}
                handleClose={handleCloseModal}
                tenantName={currentTenantName}
                tenantId={currentTenant}
                env={env}
                viewOnly={viewOnly}
              />

              <DeleteGroupVersionModal
                show={showDeleteModal}
                groupVersion={selectedGroupVersion}
                handleClose={handleCloseDeleteModal}
                handleEnvChange={handleEnvChange}
                env={env}
                tenantName={currentTenantName}
                tenantId={currentTenant}
              />
            </>
          )}
        </Container>
      ) : (
        <h5>
          <TooltipInfo
            message="Chosen Tenant does not have a project group version."
            tooltipText="You can start by adding a group under the Project Groups tab, before adding a version."
          />
        </h5>
      )}
    </>
  );
};

export default GroupVersionsListPage;
